/**
 * ActionDetails — el contexto y los botones de todo lo que NO es un
 * seguimiento: pagos, obra, documentos, tareas de IA y automatización.
 *
 * Misma gramática que la tarjeta de Seguimientos, adaptando lo que cada tipo
 * sabe de sí mismo: una tarea enseña prioridad y área, un pago enseña
 * antigüedad, una automatización enseña en qué paso se paró y por qué.
 * Quién decide eso es `lib/actionContext.js`; aquí solo se pinta.
 *
 * Los botones salen de lo que el backend declaró en `cta_secondary` —jamás de
 * una lista nuestra— y los que resuelven la tarjeta suben a la vista en vez
 * de esconderse tras los puntos suspensivos. Lo que la hace desaparecer sin
 * resolverla (descartar, ocultar) se queda en el menú a propósito.
 */
import React from "react";
import { CheckCircle } from "@phosphor-icons/react";
import { contextRowsFor, promotedSecondaries } from "@/lib/actionContext";
import { FOLLOWUP_SNOOZE_PRESETS, isoForSnoozePreset } from "@/lib/actionCta";
import {
  ACTION_BTN, ACTION_BTN_PRIMARY, ACTION_FOOTER, ActionButtonRow, ContextRows,
  SnoozeButton,
} from "@/components/actionCenter/actionCardKit";

/** La acción que resuelve la tarjeta, para destacarla entre las demás. */
const RESOLVING = new Set(["task_complete", "reply_acknowledge"]);

/**
 * Posponer necesita una FECHA. Estas mutaciones no se pueden pintar como un
 * botón suelto: sin `until` el backend recibiría un aplazamiento sin plazo.
 * Las dos que existen hoy se llaman distinto por herencia (una viene de
 * alertas, otra de seguimientos) y hacen lo mismo.
 */
const NEEDS_A_DATE = new Set(["alert_snooze", "followup_snooze"]);

const LABEL = {
  task_complete: "Hecho",
  reply_acknowledge: "Marcar atendido",
  alert_snooze: "Posponer",
  followup_snooze: "Reprogramar",
};

export default function ActionDetails({
  item, busy, onRunAction, now = new Date(), primaryCta = null,
}) {
  const id = `ac-detail-${item.action_id}`;
  const rows = contextRowsFor(item, now);
  const promoted = promotedSecondaries(item);
  if (rows.length === 0 && promoted.length === 0 && !primaryCta) return null;

  return (
    <div className={ACTION_FOOTER} data-testid={id}>
      <ContextRows rows={rows} idPrefix={id} />

      {(promoted.length > 0 || primaryCta) && (
        <ActionButtonRow idPrefix={id}>
          {promoted.map((c) => {
            const key = c.action_key;
            const label = LABEL[key] || c.label || key;
            if (NEEDS_A_DATE.has(key)) {
              return (
                <SnoozeButton
                  key={key}
                  label={label}
                  presets={FOLLOWUP_SNOOZE_PRESETS}
                  busy={busy}
                  testId={`${id}-${key}`}
                  onPick={(p) => {
                    const until = isoForSnoozePreset(p);
                    if (until) onRunAction(key, { until });
                  }}
                />
              );
            }
            const resolving = RESOLVING.has(key);
            return (
              <button
                key={key}
                type="button"
                disabled={busy}
                onClick={() => onRunAction(key)}
                className={resolving ? ACTION_BTN_PRIMARY : ACTION_BTN}
                data-testid={`${id}-${key}`}
              >
                {resolving && <CheckCircle size={14} weight="bold" />}
                {busy ? "Guardando…" : label}
              </button>
            );
          })}
          {primaryCta}
        </ActionButtonRow>
      )}
    </div>
  );
}
